import React from 'react';
import { observer } from 'mobx-react';
import { observable, action, runInAction, makeObservable } from 'mobx';
import { CirclePicker } from 'react-color';

import { type CategoryInfo } from '@root/models/CategoryInfo';
import { PromiseCompletion } from '@classes/PromiseCompletion';
import { apiService } from '@services/ApiService';
import { notificationService } from '@services/NotificationService';
import { type IModalDialogContent, type ModalButtonOptions, ModalButtonType, type ModalDialogOptions, ModalWindow, modalService } from '@components/Modal';

export type EditCategoryDialogProps = {
  category: CategoryInfo;
  noteCount: number;
  onSaved: () => void;
};

@observer
export default class EditCategoryDialog extends React.Component<EditCategoryDialogProps> implements IModalDialogContent<void> {
  @observable private _name: string;
  @observable private _color: string;
  @observable private _isRemoving = false;
  // Props are copied into local observables in the constructor — reading
  // this.props inside a reactive context (getModalOptions / render) is a MobX error.
  @observable private _noteCount: number;
  @observable.ref private _category: CategoryInfo;
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _inputRef: React.RefObject<HTMLInputElement> = React.createRef();

  constructor(props: EditCategoryDialogProps){
    super(props);
    makeObservable(this);

    const { category } = props;
    this._category = category;
    this._noteCount = props.noteCount;
    this._name = category.name;
    this._color = category.color || '#ffffff';
  }

  componentDidMount(){
    this._inputRef.current?.select();
  }

  public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
    const canRemove = this._noteCount === 0;
    const buttons: ModalButtonOptions<void>[] = [];

    buttons.push({
      type: ModalButtonType.Cancel,
      onClick: () => {
        window.close();
      }
    });
    buttons.push({
      title: 'Save',
      type: ModalButtonType.Save,
      onClick: async () => {
        await this._onSaveClick(window);
      }
    });
    if (canRemove) {
      buttons.push({
        title: 'Remove',
        type: ModalButtonType.Remove,
        isDisabled: this._isRemoving,
        onClick: () => {
          this._onRemoveClick(window);
        }
      });
    }

    return {
      title: `Edit category`,
      width: 600,
      buttons: buttons,
      loader: this._loader
    };
  }

  render(){
    const noteCount = this._noteCount;

    return (
      <>
        <div className="form-group">
          <label className="form-control-label">Name</label>
          <input className="form-control" ref={this._inputRef} autoFocus type="text" value={this._name} onChange={(e) => this._name = e.target.value} />
        </div>

        <div className="form-group">
          <label className="form-group-label">Color</label>
          <div className="form-color-picker">
            <span className="form-color-swatch" style={{ background: this._color }} />
            <CirclePicker
              color={this._color}
              onChange={(e) => action(() => { this._color = e.hex; })()}
            />
          </div>
        </div>

        {!noteCount && (
          <div className="form-group">
            <label className="form-control-label">Notes</label>
            <div className="form-hint">This category has no notes and can be removed.</div>
          </div>
        )}
        {!!noteCount && (
          <div className="form-group">
            <label className="form-control-label">Notes</label>
            <div className="form-hint">
              This category has {noteCount} note{noteCount === 1 ? '' : 's'}. Move or delete them to remove the category.
            </div>
          </div>
        )}
      </>
    );
  }

  @action.bound
  private async _onSaveClick(window: ModalWindow<void>){
    const { onSaved } = this.props;
    const category = this._category;

    await apiService.put('/api/note/category/' + category.id, {
      name: this._name,
      color: this._color
    }, {
      loader: this._loader
    });

    runInAction(() => {
      category.name = this._name;
      category.color = this._color;
    });

    onSaved();
    notificationService.success('Category was saved!');
    window.close(ModalButtonType.Save);
  }

  @action.bound
  private async _onRemoveClick(window: ModalWindow<void>){
    const { onSaved } = this.props;
    const category = this._category;

    if (this._noteCount > 0){
      return;
    }

    if (!await modalService.showConfirmation('Confirmation', `Do you really want to delete category (${category.name ?? '[No name]'})`, { color: 'danger', buttonTitle: 'Remove' })){
      return;
    }

    this._isRemoving = true;
    await apiService.delete('/api/note/category/' + category.id, {
      loader: this._loader,
      defaultErrorHandling: false
    });

    onSaved();
    notificationService.success('Category was removed!');
    window.close(ModalButtonType.Remove);
  }
}
